/**
 * Pure helpers for the weight-matrix editor — kept separate so they're trivially
 * unit-testable and reused by the grid + page.
 *
 * Ported verbatim from quill-app/quill-admin/src/components/weights/matrixUtils.ts
 * (PWE Phase 4.1). Only the type-source import changed: types now come from
 * lifescribe-admin's own quillAdminService client instead of quill-admin's api.ts.
 */

import { WeightMatrix, WeightCell } from '../../../services/quillAdminService';

export const cellKey = (channel: string, dimension_key: string): string =>
  `${channel}::${dimension_key}`;

/** Humanize a snake_case channel id → "Direct Conversation". */
export function humanizeChannel(channel: string): string {
  return channel
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Distinct channel ids from a matrix, in first-seen order. */
export function channelsOf(matrix: WeightMatrix): string[] {
  const seen: string[] = [];
  for (const c of matrix.cells) {
    if (!seen.includes(c.channel)) seen.push(c.channel);
  }
  return seen.sort();
}

/** Build a { "channel::dim": weight } lookup from a matrix. */
export function toValueMap(matrix: WeightMatrix): Record<string, number> {
  const map: Record<string, number> = {};
  for (const c of matrix.cells) {
    map[cellKey(c.channel, c.dimension_key)] = c.weight;
  }
  return map;
}

/** Clamp + round to 3 decimals; returns null for non-finite input. */
export function normalizeWeight(raw: string | number): number | null {
  const n = typeof raw === 'number' ? raw : parseFloat(raw);
  if (!Number.isFinite(n)) return null;
  const clamped = Math.min(1, Math.max(0, n));
  return Math.round(clamped * 1000) / 1000;
}

/**
 * Assemble the full cell array to POST as a new version. Every channel×dimension
 * pair present in the source matrix is emitted (the backend rejects an
 * incomplete matrix), taking edited values where provided, else the original.
 */
export function buildFullMatrix(
  matrix: WeightMatrix,
  edits: Record<string, string>
): WeightCell[] {
  return matrix.cells.map((c) => {
    const k = cellKey(c.channel, c.dimension_key);
    const edited = edits[k];
    const value =
      edited !== undefined && normalizeWeight(edited) !== null
        ? (normalizeWeight(edited) as number)
        : c.weight;
    return { channel: c.channel, dimension_key: c.dimension_key, weight: value };
  });
}

/** True when at least one edit differs from the matrix's current value. */
export function hasEdits(matrix: WeightMatrix, edits: Record<string, string>): boolean {
  const base = toValueMap(matrix);
  return Object.entries(edits).some(([k, v]) => {
    const n = normalizeWeight(v);
    return n !== null && n !== base[k];
  });
}
